import { useEffect, useState } from "react";
import { api, inr } from "../lib/api";
import { PageHead, Badge } from "../components/ui-bits";
import { toast } from "sonner";
import { CreditCard, Receipt, DownloadSimple } from "@phosphor-icons/react";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(window.Razorpay);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(window.Razorpay);
    s.onerror = () => resolve(null);
    document.body.appendChild(s);
  });
}

export default function Finance() {
  const [invoices, setInvoices] = useState([]);
  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const payNow = async (v) => {
    const { data: cfg } = await api.get("/payments/config");
    if (!cfg.configured) {
      toast.info("Razorpay not configured yet — add your API keys to enable live UPI/card/net-banking checkout.");
      return;
    }
    try {
      const { data } = await api.post("/payments/create-order", { amount: v.amount, invoice: v.number, receipt: v.number });
      const Razorpay = await loadRazorpayScript();
      if (!Razorpay) { toast.error("Could not load payment gateway"); return; }
      new Razorpay({
        key: data.key_id, amount: data.amount, currency: "INR",
        name: "Virtualeena", description: v.number, order_id: data.order_id,
        theme: { color: "#002FA7" },
        handler: async (resp) => { await api.post("/payments/verify", { ...resp, invoice: v.number }); toast.success("Payment successful"); load(); },
      }).open();
    } catch (e) { toast.error("Payment could not be started"); }
  };

  const outstanding = invoices.filter((v) => v.status === "Outstanding").reduce((a, v) => a + v.amount, 0);

  const downloadInvoice = async (v) => {
    try {
      const res = await api.get(`/invoices/${encodeURIComponent(v.number)}/pdf?amount=${v.amount}&plan=${encodeURIComponent(v.plan || "")}&date=${encodeURIComponent(v.date || "")}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${v.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate invoice PDF"); }
  };

  return (
    <div>
      <PageHead eyebrow="Billing & Payments" title="Invoices & Payments" />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-5"><p className="label mb-1">Total Invoices</p><p className="font-head font-black text-2xl tnum">{invoices.length}</p></div>
        <div className="card p-5"><p className="label mb-1">Outstanding</p><p className="font-head font-black text-2xl tnum text-[#FF3B30]">{inr(outstanding)}</p></div>
        <div className="card p-5"><p className="label mb-1">Paid</p><p className="font-head font-black text-2xl tnum text-[#00A86B]">{invoices.filter((v) => v.status === "Paid").length}</p></div>
      </div>

      <div className="card p-6">
        <p className="label mb-4 flex items-center gap-2"><CreditCard size={16} color="#002FA7" /> Invoices & Payments</p>
        <table className="w-full text-sm">
          <thead><tr className="label text-left border-b border-black/10">
            <th className="pb-2">Invoice</th><th className="pb-2">Plan</th><th className="pb-2">Date</th>
            <th className="pb-2 text-right">Amount</th><th className="pb-2 text-right">Status</th></tr></thead>
          <tbody>
            {invoices.map((v, i) => (
              <tr key={i} data-testid={`invoice-${i}`} className="border-b border-black/5">
                <td className="py-2.5 font-medium flex items-center gap-2"><Receipt size={15} className="text-[#888]" /> {v.number}</td>
                <td className="py-2.5 text-[#555]">{v.plan}</td>
                <td className="py-2.5 text-[#888] tnum">{v.date}</td>
                <td className="py-2.5 text-right tnum">{inr(v.amount)}</td>
                <td className="py-2.5 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <button data-testid={`invoice-pdf-${i}`} onClick={() => downloadInvoice(v)} title="Download PDF"
                      className="text-[#002FA7] hover:bg-[#002FA7]/10 p-1.5 rounded-sm transition-colors"><DownloadSimple size={16} /></button>
                    {v.status === "Outstanding"
                      ? <button data-testid={`pay-${i}`} onClick={() => payNow(v)}
                          className="text-xs bg-[#002FA7] text-white px-3 py-1 rounded-sm hover:bg-[#00227A] transition-colors">Pay Now</button>
                      : <Badge status={v.status} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
